import {
  ExpandableGroup,
  Field,
  Loader,
  PopupTooltip,
  Select,
  Toggle,
} from '@teamsnap/teamsnap-ui';
import * as React from 'react';
import { GroupCapacityRow } from './types';

import { sortByName } from 'core/utils';
import './GroupCapacity.scss';

type GroupCapacityProps = {
  orgId: number;
  rows: GroupCapacityRow[];
  setRowsCallback: React.Dispatch<React.SetStateAction<GroupCapacityRow[]>>;
  setExpandedCallback(rowId: number): void;
};

const GroupCapacity = ({
  orgId,
  rows,
  setRowsCallback,
  setExpandedCallback,
}: GroupCapacityProps) => {
  const [capacityLimit, setCapacityLimit] = React.useState<boolean>(false);
  const [isInitialized, setIsInitialized] = React.useState<boolean>(false);

  const groupRefs = rows?.map(() => React.createRef<HTMLDivElement>());

  React.useEffect(() => {
    if (!isInitialized && rows?.length > 0) {
      if (
        rows?.some((r) => r.maxCapacity || r.maxCapacity === 0) ||
        rows?.some((r) => r.toggled)
      ) {
        setCapacityLimit(true);
      } else {
        setCapacityLimit(false);
      }
      setIsInitialized(true);
    }

    // If we have errors focus on the top most error and scroll to it
    if (rows?.length > 0) {
      const errorIndex = rows.findIndex((row) => row.error);

      if (errorIndex > -1) {
        // We force a cycle refresh so that the element is opened before we try to scroll to it
        setTimeout(() => {
          groupRefs[errorIndex]?.current?.scrollIntoView({
            behavior: 'smooth',
          });
        }, 0);
      }
    }
  }, [rows]);

  const options = [
    {
      label: 'No capacity limit',
      value: 'no_capacity_limit',
    },
    {
      label: 'Option-based capacity limits',
      value: 'capacity_limit',
    },
  ];

  const handleToggle = (rowId: number) => {
    // Find the index of the targeted row
    const targetIndex = rows.findIndex((row) => row.id === rowId);
    // Make a copy since we should not try to change a passed param
    const rowsCopy = [...rows];

    // Toggle the value at the proper index
    rowsCopy[targetIndex].toggled = !rowsCopy[targetIndex].toggled;
    if (!rowsCopy[targetIndex].toggled) {
      rowsCopy[targetIndex].maxCapacity = null; // reset maxCapacity if toggle is off
    }

    // Update parent state
    setRowsCallback(rowsCopy);
  };

  const handleUpdate = (
    rowId: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.value.length > 6) {
      event.preventDefault();

      return false;
    }

    const { value } = event?.target;

    // Find the index of the targeted row
    const targetIndex = rows.findIndex((row) => row.id === rowId);
    // Make a copy since we should not try to change a passed param
    const rowsCopy = [...rows];

    rowsCopy[targetIndex].error = null;
    rowsCopy[targetIndex].maxCapacity = value
      ? Math.abs(parseInt(value, 10))
      : null;

    // Update parent state
    setRowsCallback(rowsCopy);
  };

  const handleWaitlistChange = (rowId: number, value: boolean) => {
    // Find the index of the targeted row
    const targetIndex = rows.findIndex((row) => row.id === rowId);
    // Make a copy since we should not try to change a passed param
    const rowsCopy = [...rows];
    rowsCopy[targetIndex].waitlistAllowed = value;

    // Update parent state
    setRowsCallback(rowsCopy);
  };

  const toggleOffAllRows = () => {
    rows.forEach((r) => {
      // Find the index of the targeted row
      const targetIndex = rows.findIndex((row) => row.id === r.id);
      // Make a copy since we should not try to change a passed param
      const rowsCopy = [...rows];

      // Toggle the value at the proper index
      rowsCopy[targetIndex].toggled = false;
      rowsCopy[targetIndex].maxCapacity = null;

      // Update parent state
      setRowsCallback(rowsCopy);
    });
  };

  const capacityDetailsHelperText = (row: GroupCapacityRow) => {
    if (row.maxCapacity !== null && row.maxCapacity >= 0) {
      return `Capacity limit: ${row.maxCapacity}`;
    }

    return 'No capacity limit';
  };

  if (!rows || rows?.length === 0) {
    return <Loader type="spin" text="Loading" />;
  }

  return (
    <div className="GroupCapacity u-flex u-flexCol">
      <Select
        inputProps={{
          onChange: (e: React.ChangeEvent<HTMLSelectElement>) => {
            if (e.target.value === options[0].value) {
              setCapacityLimit(false);
              toggleOffAllRows();
            } else {
              setCapacityLimit(true);
            }
          },
          value: capacityLimit ? 'capacity_limit' : 'no_capacity_limit',
        }}
        mods="u-spaceBottomSm"
        name="capacity-limit"
        options={options}
        testId="GroupCapacity--limit-select"
      />

      {capacityLimit
        ? rows
            ?.filter((r) => r.active)
            ?.sort(sortByName)
            ?.map((row, index) => (
              <div
                key={`${row.name.split(' ').join('-')}-${row.id}`}
                data-testid={`capacityGroupTile-${index}`}
              >
                <ExpandableGroup
                  id={row.id}
                  isExpanded={row.expanded}
                  label={row.name}
                  groupHelperText={
                    <span
                      data-testid={`GroupCapacity--group-helper-text-${index}`}
                    >
                      {capacityDetailsHelperText(row)}
                    </span>
                  }
                  onExpandedChange={() => setExpandedCallback(row.id)}
                >
                  <div
                    ref={groupRefs[index]}
                    className="u-flex u-flexCol u-padSidesXxl u-padBottomMd"
                  >
                    <div className="u-flex u-flexAlignItemsCenter u-spaceBottomSm">
                      <div className="u-spaceRightMd u-fontSizeSm">
                        Capacity limit
                      </div>

                      <Toggle
                        name={`capacityToggle-${index}`}
                        inputProps={{
                          checked: row.toggled,
                          onChange: () => handleToggle(row.id),
                        }}
                      />
                    </div>

                    {row.toggled ? (
                      <div className="u-padTopXs u-padBottomSm u-padSidesMd u-bgNeutral2 u-fontSizeSm">
                        <p>
                          Maximum spots
                          <span className="u-colorNegative">*</span>
                        </p>
                        <input
                          type="text"
                          pattern="^[0-9]*$"
                          data-testid="group-capacity-input"
                          className={`
                        u-border u-borderColorDefault u-padSidesSm u-borderRadiusMd
                        u-spaceBottomSm ${
                          row.error?.length > 0 ? 'u-borderColorNegative' : null
                        }
                      `}
                          placeholder="e.g. 200"
                          onChange={(e) => handleUpdate(row.id, e)}
                          value={
                            row.maxCapacity || row.maxCapacity === 0
                              ? row.maxCapacity
                              : ''
                          }
                        />
                        {row.error?.length > 0 ? (
                          <p className="u-spaceBottomSm u-colorNegative">
                            {
                              rows[
                                rows.findIndex((row) => row.error?.length > 0)
                              ].error
                            }
                          </p>
                        ) : null}
                        <>
                          <p className="u-spaceTopSm">
                            Once capacity limit is reached
                          </p>

                          <Field
                            mods="u-padTopMd"
                            style={{ marginRight: '12px' }}
                            type="radio"
                            formFieldProps={{
                              text: 'Do not allow checkout.',
                              checked: !row.waitlistAllowed,
                              onClick: () =>
                                handleWaitlistChange(row.id, false),
                            }}
                            name={`capacity-limit-${row.id}`}
                          />

                          {!row.waitlistAllowed ? (
                            <PopupTooltip
                              mods="u-padLeftLg"
                              text={
                                <div>
                                  <p>
                                    Eligible registrants can view options at
                                    full capacity and will be unable to
                                    add&nbsp; them to their cart.
                                  </p>

                                  <p className="u-padTopMd">
                                    Selected options that reach capacity before
                                    checkout will trigger a notification&nbsp;
                                    that the option is full and will be removed
                                    from the cart.
                                  </p>
                                </div>
                              }
                              ariaDescribeBy="Tooltip Example"
                            >
                              What will registrants see?
                            </PopupTooltip>
                          ) : null}

                          <Field
                            mods="u-padTopMd"
                            type="radio"
                            style={{ marginRight: '12px' }}
                            formFieldProps={{
                              text: 'Allow participant to join waitlist.',
                              checked: row.waitlistAllowed,
                              onClick: () => handleWaitlistChange(row.id, true),
                            }}
                            name={`capacity-limit-${row.id}`}
                          />

                          {row.waitlistAllowed ? (
                            <PopupTooltip
                              mods="u-padLeftLg"
                              text={
                                <div>
                                  <p>
                                    Eligible registrants can view options at
                                    full capacity and will be unable to
                                    add&nbsp; them to their cart but can opt to
                                    join the waitlist.
                                  </p>

                                  <p className="u-padTopMd">
                                    Selected options that reach capacity before
                                    checkout will trigger a notification&nbsp;
                                    that the option is full. Registrants can
                                    then choose to empty their cart or
                                    join&nbsp; the waitlist.
                                  </p>
                                </div>
                              }
                              ariaDescribeBy="Tooltip Example"
                            >
                              What will registrants see?
                            </PopupTooltip>
                          ) : null}
                        </>
                      </div>
                    ) : null}
                  </div>
                </ExpandableGroup>
              </div>
            ))
        : null}
    </div>
  );
};

export default GroupCapacity;
